import { useRef } from 'react';
import type { AvatarBlock } from '@blogforge/shared';
import type { DragPath } from '../../types';
import { useEditorStore } from '../../stores/editorStore';
import VariableInfusedTextEditor, {
  type RichTextEditorHandle,
} from '../tiptap/VariableInfusedTextEditor';
import { Switch } from '../ui/Switch';

interface AvatarInspectorProps {
  block: AvatarBlock;
  path: DragPath;

  variableNameOptions?: string[];
}

// Background colour, border and alignment come from the shared style panel.
export function AvatarInspector({ block, path, variableNameOptions = [] }: AvatarInspectorProps) {
  const richTextRef = useRef<RichTextEditorHandle>(null);
  const linkRef = useRef<RichTextEditorHandle>(null);
  const updateBlock = useEditorStore((s) => s.updateBlock);
  const attrs = block.attrs;

  const size = attrs.size ?? attrs.image.width ?? 64;

  const setImage = (patch: Partial<AvatarBlock['attrs']['image']>) => {
    updateBlock(block.id, { attrs: { ...attrs, image: { ...attrs.image, ...patch } } }, path);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="field-label">Image Url</label>
        <VariableInfusedTextEditor
          value={attrs.image.src}
          onChange={(val, srcIsMustache) => setImage({ src: val, srcIsMustache })}
          placeholder="Add a url... Use @ to reference a variable"
          options={variableNameOptions}
          ref={richTextRef}
          tagsOnly={true}
          maxTags={1}
        />
      </div>

      <div>
        <Switch
          checked={attrs.image.skipOptimize}
          label="Do not convert to webp"
          onChange={(checked) => setImage({ skipOptimize: checked })}
        />
      </div>

      <div>
        <label className="field-label" htmlFor="avatar-alt">
          Alt text
        </label>
        <input
          id="avatar-alt"
          type="text"
          className="field-input"
          placeholder="Who this is"
          value={attrs.image.alt ?? ''}
          onChange={(e) => setImage({ alt: e.target.value })}
        />
      </div>

      <div>
        <label className="field-label">Link</label>
        <VariableInfusedTextEditor
          value={attrs.href ?? ''}
          onChange={(val, hrefIsMustache) =>
            updateBlock(
              block.id,
              { attrs: { ...attrs, href: val || undefined, hrefIsMustache } },
              path,
            )
          }
          placeholder="Optional — where a click goes. Use @ to reference a variable"
          options={variableNameOptions}
          ref={linkRef}
          tagsOnly={true}
          maxTags={1}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="avatar-size">
            Avatar size (px)
          </label>
          <input
            id="avatar-size"
            type="number"
            min={16}
            max={600}
            className="field-input"
            value={size}
            onChange={(e) =>
              updateBlock(
                block.id,
                {
                  attrs: {
                    ...attrs,
                    size: e.target.value ? Number(e.target.value) : 64,
                    // An avatar saved before `size` existed kept its frame in
                    // image.width; once the frame has its own size, that
                    // width would shrink the picture instead.
                    image: attrs.size === undefined ? { ...attrs.image, width: undefined, height: undefined } : attrs.image,
                  },
                },
                path,
              )
            }
          />
        </div>

        <div>
          <label className="field-label" htmlFor="avatar-radius">
            Radius
          </label>
          <input
            id="avatar-radius"
            type="text"
            className="field-input"
            placeholder="50%"
            value={attrs.radius ?? ''}
            onChange={(e) =>
              updateBlock(block.id, { attrs: { ...attrs, radius: e.target.value } }, path)
            }
          />
        </div>
      </div>

      {/* Empty = the picture fills the avatar (a photo); set = it sits centred
          in it at that size (an icon in a badge). */}
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="avatar-image-width">
            Image width (px)
          </label>
          <input
            id="avatar-image-width"
            type="number"
            min={1}
            max={size}
            className="field-input"
            placeholder="Fill"
            value={attrs.size === undefined ? '' : (attrs.image.width ?? '')}
            onChange={(e) =>
              updateBlock(
                block.id,
                {
                  attrs: {
                    ...attrs,
                    size,
                    image: {
                      ...attrs.image,
                      width: e.target.value ? Number(e.target.value) : undefined,
                    },
                  },
                },
                path,
              )
            }
          />
        </div>

        <div>
          <label className="field-label" htmlFor="avatar-image-height">
            Image height (px)
          </label>
          <input
            id="avatar-image-height"
            type="number"
            min={1}
            max={size}
            className="field-input"
            placeholder="Same as width"
            value={attrs.size === undefined ? '' : (attrs.image.height ?? '')}
            onChange={(e) =>
              updateBlock(
                block.id,
                {
                  attrs: {
                    ...attrs,
                    size,
                    image: {
                      ...attrs.image,
                      height: e.target.value ? Number(e.target.value) : undefined,
                    },
                  },
                },
                path,
              )
            }
          />
        </div>
      </div>
    </div>
  );
}
